import { allStickersWithCocaCola } from '../data/albumCatalog';
import { countries } from '../data/countries';

export type ImportSection = 'have' | 'want';

export type ImportItem = {
  stickerId: string;
  copies: number;
  section: ImportSection;
};

export type ImportResult = {
  ok: ImportItem[];
  unknown: string[];
  errors: string[];
  // true si el texto tenia al menos un encabezado de seccion reconocido.
  // false = lista sin titulo, la UI debe preguntar al usuario que tipo es.
  detectedSections: boolean;
};

export type ImportOptions = {
  // Si true, items en seccion 'Repetidas' sin xN explicito se interpretan como copies=2.
  // Si false, copies=1 (solo pegada).
  assumeRepeatedDoublesCount?: boolean;
  // Si esta presente, fuerza toda la entrada a esa seccion ignorando los
  // encabezados 'Repetidas'/'Busco'. Usado cuando la UI tiene campos separados
  // y no necesita auto-deteccion.
  forceSection?: ImportSection;
};

const VALID_STICKER_IDS = new Set(allStickersWithCocaCola.map((s) => s.id));

const COUNTRY_CODES = new Set(countries.map((c) => c.code.toUpperCase()));

// Aliases de codigos FIFA / variantes a 3-letras que usamos internamente.
const COUNTRY_ALIAS: Record<string, string> = {
  JPN: 'JAP',
  CHE: 'SUI',
  CH: 'SUI',
  NL: 'NED',
  HOL: 'NED',
  DE: 'GER',
  DEU: 'GER',
  ALE: 'GER',
  ES: 'ESP',
  FR: 'FRA',
  PT: 'POR',
  PRT: 'POR',
  UK: 'ENG',
  GB: 'ENG',
  GBR: 'ENG',
  US: 'USA',
  SA: 'KSA',
  SAU: 'KSA',
  DZA: 'ALG',
  DZ: 'ALG',
  AR: 'ARG',
  BR: 'BRA',
  MX: 'MEX',
  ZA: 'RSA',
  ZAF: 'RSA',
  KR: 'KOR',
  CZK: 'CZE',
  TR: 'TUR',
  IR: 'IRN',
  IRI: 'IRN',
  IRA: 'IRN',
  COG: 'COD',
  CGO: 'COD',
  RDC: 'COD',
};

// Nombres completos (ES/EN/local) en mayusculas sin acentos → codigo 3-letras.
const COUNTRY_NAME_TO_CODE: Record<string, string> = {
  MEXICO: 'MEX',
  'SOUTH AFRICA': 'RSA',
  SUDAFRICA: 'RSA',
  'SOUTH KOREA': 'KOR',
  'COREA DEL SUR': 'KOR',
  COREA: 'KOR',
  KOREA: 'KOR',
  'CZECH REPUBLIC': 'CZE',
  CZECHIA: 'CZE',
  'REPUBLICA CHECA': 'CZE',
  CHEQUIA: 'CZE',
  CHECA: 'CZE',
  CANADA: 'CAN',
  'BOSNIA AND HERZEGOVINA': 'BIH',
  'BOSNIA Y HERZEGOVINA': 'BIH',
  'BOSNIA HERZEGOVINA': 'BIH',
  BOSNIA: 'BIH',
  QATAR: 'QAT',
  CATAR: 'QAT',
  SWITZERLAND: 'SUI',
  SUIZA: 'SUI',
  BRAZIL: 'BRA',
  BRASIL: 'BRA',
  MOROCCO: 'MAR',
  MARRUECOS: 'MAR',
  HAITI: 'HAI',
  SCOTLAND: 'SCO',
  ESCOCIA: 'SCO',
  'UNITED STATES': 'USA',
  'ESTADOS UNIDOS': 'USA',
  EEUU: 'USA',
  PARAGUAY: 'PAR',
  AUSTRALIA: 'AUS',
  TURKEY: 'TUR',
  TURQUIA: 'TUR',
  TURKIYE: 'TUR',
  GERMANY: 'GER',
  ALEMANIA: 'GER',
  DEUTSCHLAND: 'GER',
  CURACAO: 'CUW',
  CURAZAO: 'CUW',
  'IVORY COAST': 'CIV',
  'COSTA DE MARFIL': 'CIV',
  ECUADOR: 'ECU',
  NETHERLANDS: 'NED',
  HOLANDA: 'NED',
  'PAISES BAJOS': 'NED',
  HOLLAND: 'NED',
  JAPAN: 'JAP',
  JAPON: 'JAP',
  SWEDEN: 'SWE',
  SUECIA: 'SWE',
  TUNISIA: 'TUN',
  TUNEZ: 'TUN',
  BELGIUM: 'BEL',
  BELGICA: 'BEL',
  EGYPT: 'EGY',
  EGIPTO: 'EGY',
  IRAN: 'IRN',
  'NEW ZEALAND': 'NZL',
  'NUEVA ZELANDA': 'NZL',
  'NUEVA ZELANDIA': 'NZL',
  SPAIN: 'ESP',
  ESPANA: 'ESP',
  'CAPE VERDE': 'CPV',
  'CABO VERDE': 'CPV',
  'SAUDI ARABIA': 'KSA',
  'ARABIA SAUDITA': 'KSA',
  'ARABIA SAUDI': 'KSA',
  URUGUAY: 'URU',
  FRANCE: 'FRA',
  FRANCIA: 'FRA',
  SENEGAL: 'SEN',
  IRAQ: 'IRQ',
  IRAK: 'IRQ',
  NORWAY: 'NOR',
  NORUEGA: 'NOR',
  ARGENTINA: 'ARG',
  ALGERIA: 'ALG',
  ARGELIA: 'ALG',
  AUSTRIA: 'AUT',
  JORDAN: 'JOR',
  JORDANIA: 'JOR',
  PORTUGAL: 'POR',
  'DR CONGO': 'COD',
  'RD CONGO': 'COD',
  'REPUBLICA DEMOCRATICA DEL CONGO': 'COD',
  CONGO: 'COD',
  UZBEKISTAN: 'UZB',
  COLOMBIA: 'COL',
  ENGLAND: 'ENG',
  INGLATERRA: 'ENG',
  CROATIA: 'CRO',
  CROACIA: 'CRO',
  GHANA: 'GHA',
  PANAMA: 'PAN',
};

// Flag emoji → codigo. Construido desde countries.
const FLAG_TO_CODE: Record<string, string> = (() => {
  const map: Record<string, string> = {};
  for (const c of countries) {
    if (c.flag) map[c.flag] = c.code.toUpperCase();
  }
  return map;
})();

// Nombres ordenados por longitud descendente para matching greedy correcto.
const COUNTRY_NAME_ALIASES: string[] = Object.keys(COUNTRY_NAME_TO_CODE).sort(
  (a, b) => b.length - a.length,
);

function normalizeText(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toUpperCase();
}

const STRIP_EMOJI =
  /[\u{1F1E6}-\u{1F1FF}\u{1F300}-\u{1F5FF}\u{1F600}-\u{1F64F}\u{1F680}-\u{1F6FF}\u{1F700}-\u{1F77F}\u{1F900}-\u{1F9FF}\u{1FA00}-\u{1FAFF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{FE0F}\u{200D}\u{E0020}-\u{E007F}\u{1F3F4}]/gu;

function stripEmojis(line: string): string {
  return line.replace(STRIP_EMOJI, '').replace(/\s+/g, ' ').trim();
}

function detectFlagInLine(line: string): string | null {
  for (const flag in FLAG_TO_CODE) {
    if (line.includes(flag)) return FLAG_TO_CODE[flag];
  }
  return null;
}

function normalizeParenQuantity(line: string): string {
  return line.replace(/\(\s*x?\s*(\d+)\s*x?\s*\)/gi, ' x$1');
}

function stripDecorations(line: string): string {
  return line.replace(/[*_`~]/g, '').trim();
}

function cleanSlotToken(raw: string): string {
  let s = raw.trim();
  // Prefijos descartables: : - # nº n° no. num. nro.
  s = s.replace(/^[:\-]+\s*/, '');
  s = s.replace(/^(?:#|n[º°o]\.?|num\.?|nro\.?)\s*/i, '');
  s = s.replace(/[.,;:!?)\]º°]+$/g, '');
  return s.trim();
}

function isHaveSectionHeader(line: string): boolean {
  const lower = line.toLowerCase();
  return /\b(repetidas?|repetidos?|tenidas?|mi\s*album|tengo|sobran|sobrantes?|spares?|extras?|duplicates?|swaps?|trades?|intercambios?|i\s*have|have|cromos\s*repetid|figuritas\s*repetid|me\s*sobran|para\s*cambio|cambios?)\b/.test(
    lower,
  );
}

function isWantSectionHeader(line: string): boolean {
  const lower = line.toLowerCase();
  return /\b(busco|me\s*faltan|wishlist|necesito|faltantes?|faltan|missing|i\s*need|i\s*want|want|needs?|looking\s*for|me\s*falta|mis\s*faltantes?|lo\s*que\s*me\s*falta)\b/.test(
    lower,
  );
}

function normalizePrefix(prefix: string): string {
  const upper = prefix.toUpperCase();
  return COUNTRY_ALIAS[upper] ?? upper;
}

// Construye el sticker id interno desde prefix + slot.
function buildStickerId(prefix: string, slot: string): string | null {
  const cleanSlot = slot.trim();
  if (prefix === 'FWC' || prefix === 'FW') {
    if (cleanSlot === '00' || cleanSlot === '0') return '00';
    const n = parseInt(cleanSlot, 10);
    if (Number.isNaN(n) || n < 1 || n > 19) return null;
    return `FW${n}`;
  }
  if (prefix === 'CC') {
    const n = parseInt(cleanSlot, 10);
    if (Number.isNaN(n) || n < 1 || n > 14) return null;
    return `CC${n}`;
  }
  if (COUNTRY_CODES.has(prefix)) {
    const n = parseInt(cleanSlot, 10);
    if (Number.isNaN(n) || n < 1 || n > 20) return null;
    return `${prefix}${n}`;
  }
  return null;
}

function isKnownPrefix(prefix: string): boolean {
  return COUNTRY_CODES.has(prefix) || prefix === 'FWC' || prefix === 'FW' || prefix === 'CC';
}

type InlineToken = {
  prefix: string;
  slot: string;
  copies: number;
  explicit: boolean;
  raw: string;
};

// Extrae tokens completos inline: "MEX2", "MEX2 x3", "FWC9 x2", "00 x1", "MEX1-5", "MEX1-5x2".
function tokenizeInlineCodes(line: string): InlineToken[] {
  const out: InlineToken[] = [];
  // Rangos primero.
  const reRange = /(?:^|[^A-Za-z0-9])([A-Z]{2,4})(\d{1,2})\s*-\s*(\d{1,2})(?:\s*x\s*(\d+))?(?![A-Za-z0-9])/gi;
  const consumed: Array<[number, number]> = [];
  let m: RegExpExecArray | null;
  while ((m = reRange.exec(line)) !== null) {
    const prefix = normalizePrefix(m[1]);
    if (!isKnownPrefix(prefix)) continue;
    const from = parseInt(m[2], 10);
    const to = parseInt(m[3], 10);
    if (Number.isNaN(from) || Number.isNaN(to) || from > to) continue;
    const explicit = m[4] !== undefined;
    const copies = explicit ? Math.max(1, parseInt(m[4]!, 10) || 1) : 1;
    for (let n = from; n <= to; n++) {
      out.push({ prefix, slot: String(n), copies, explicit, raw: `${prefix}${n}` });
    }
    consumed.push([m.index, m.index + m[0].length]);
  }
  let cleaned = line;
  for (const [start, end] of consumed.reverse()) {
    cleaned = cleaned.slice(0, start) + ' '.repeat(end - start) + cleaned.slice(end);
  }
  const reCoded = /(?:^|[^A-Za-z0-9])([A-Z]{2,4})(\d{1,2})(?:\s*x\s*(\d+))?(?![A-Za-z0-9])/gi;
  while ((m = reCoded.exec(cleaned)) !== null) {
    const prefix = normalizePrefix(m[1]);
    if (!isKnownPrefix(prefix)) continue;
    const explicit = m[3] !== undefined;
    const copies = explicit ? Math.max(1, parseInt(m[3]!, 10) || 1) : 1;
    out.push({ prefix, slot: m[2], copies, explicit, raw: `${prefix}${m[2]}` });
  }
  const reZero = /(?:^|[^A-Za-z0-9])(00)(?:\s*x\s*(\d+))?(?![A-Za-z0-9])/g;
  while ((m = reZero.exec(cleaned)) !== null) {
    const explicit = m[2] !== undefined;
    const copies = explicit ? Math.max(1, parseInt(m[2]!, 10) || 1) : 1;
    out.push({ prefix: 'FW', slot: '00', copies, explicit, raw: '00' });
  }
  return out;
}

function isIgnorableLine(line: string): boolean {
  if (!line) return true;
  if (line.startsWith('#')) return true;
  if (/^[─-╿=\-_·•\s]+$/.test(line)) return true;
  if (/^(hola|buenas|buenos d[ií]as|buenas tardes|gracias|saludos)\b/i.test(line)) return true;
  if (/cambiemos cromos|estos son mis cromos|por si est[aá]n interesados/i.test(line)) {
    return true;
  }
  if (/^p[aá]?g\.?\s*\d+/i.test(line)) return true;
  return false;
}

// Linea que solo contiene numeros y separadores (sin letras salvo 'x' multiplicador y prefijos
// como 'n', 'N' permitidos por cleanSlotToken). Tambien permite ':' inicial residual de
// "🇲🇽: 1, 2, 3" donde el flag fue stripeado.
function isSlotOnlyLine(line: string): boolean {
  const stripped = line.replace(/\bx\d+\b/gi, '').replace(/[#nN][°ºo]?\s*/g, '');
  return /^[\s\d,;:|/\-().]+$/.test(stripped) && /\d/.test(stripped);
}

type SlotToken =
  | { kind: 'single'; slot: string; copies: number; explicit: boolean }
  | { kind: 'range'; from: number; to: number; copies: number; explicit: boolean };

function parseSlotToken(raw: string): SlotToken | { kind: 'error'; raw: string } {
  const token = cleanSlotToken(raw);
  if (!token) return { kind: 'error', raw };

  let copies = 1;
  let explicit = false;
  let body = token;
  const xMatch = token.match(/^(.+?)\s*x\s*(\d+)$/i);
  if (xMatch) {
    const c = parseInt(xMatch[2], 10);
    if (Number.isNaN(c) || c < 1) return { kind: 'error', raw };
    copies = c;
    explicit = true;
    body = xMatch[1].trim();
  }

  const rangeMatch = body.match(/^(\d+)\s*-\s*(\d+)$/);
  if (rangeMatch) {
    const from = parseInt(rangeMatch[1], 10);
    const to = parseInt(rangeMatch[2], 10);
    if (Number.isNaN(from) || Number.isNaN(to) || from > to) return { kind: 'error', raw };
    return { kind: 'range', from, to, copies, explicit };
  }

  if (/^\d+$/.test(body)) {
    return { kind: 'single', slot: body, copies, explicit };
  }

  return { kind: 'error', raw };
}

// Divide la sub-cadena de slots aceptando separadores varios: , ; | / y runs de espacio.
function splitSlotString(s: string): string[] {
  // Primero divide en separadores duros (, ; | /).
  const hard = s.split(/[,;|/]+/);
  const out: string[] = [];
  for (const chunk of hard) {
    const trimmed = chunk.trim();
    if (!trimmed) continue;
    // Si el chunk contiene multiples grupos separados por espacios y son todos numericos
    // (ej "1 5 10" o "1x2 5 10"), dividir tambien por espacios.
    if (/\s/.test(trimmed)) {
      const parts = trimmed.split(/\s+/);
      const allNumeric = parts.every((p) => /^\d+(-\d+)?(x\d+)?$/i.test(cleanSlotToken(p)));
      if (allNumeric) {
        for (const p of parts) out.push(p);
        continue;
      }
    }
    out.push(trimmed);
  }
  return out;
}

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// Intenta extraer un encabezado de pais al inicio de la linea.
function matchCountryHeader(
  line: string,
): { prefix: string; rest: string; explicitSeparator: boolean } | null {
  // 1) Codigo corto seguido de separador (rest opcional, puede estar vacio).
  const reShort = /^([A-Za-z]{2,4})\s*([:\-—])\s*(.*)$/;
  const mShort = line.match(reShort);
  if (mShort) {
    const code = normalizePrefix(mShort[1]);
    if (isKnownPrefix(code)) {
      return { prefix: code, rest: mShort[3] ?? '', explicitSeparator: true };
    }
  }
  // 1b) Codigo corto seguido de espacio + numeros (sin colon): "MEX 1, 5, 10" o "MEX 1 5 10".
  const reShortSpace = /^([A-Za-z]{2,4})\s+(\d.*)$/;
  const mShortSpace = line.match(reShortSpace);
  if (mShortSpace) {
    const code = normalizePrefix(mShortSpace[1]);
    if (isKnownPrefix(code)) {
      return { prefix: code, rest: mShortSpace[2], explicitSeparator: false };
    }
  }
  // 2) Nombre largo (Mexico, Holanda, South Korea, ...).
  const upperLine = normalizeText(line);
  for (const name of COUNTRY_NAME_ALIASES) {
    if (!upperLine.startsWith(name)) continue;
    const code = COUNTRY_NAME_TO_CODE[name];
    const after = upperLine.slice(name.length);
    // Boundary: debe seguir separador, espacio, fin de linea o digito.
    if (after.length > 0 && /[A-Z]/.test(after[0])) continue;
    // Toma resto desde la cadena original alineada por longitud de la version sin acentos.
    // Como removemos diacriticos via NFD, los indices coinciden (los combining marks salen).
    const restRaw = line.normalize('NFD').replace(/[̀-ͯ]/g, '').slice(name.length);
    const cleanedRest = restRaw.replace(/^\s*[:\-—]?\s*/, '');
    return { prefix: code, rest: cleanedRest, explicitSeparator: /^[:\-—]/.test(restRaw.trim()) };
  }
  return null;
}

// Marker para silenciar unused: escapeRegex se mantiene por si lo usa codigo externo.
void escapeRegex;

export function parseAlbumImport(text: string, options: ImportOptions = {}): ImportResult {
  const assumeDouble = options.assumeRepeatedDoublesCount ?? true;
  const forceSection = options.forceSection;
  const result: ImportResult = { ok: [], unknown: [], errors: [], detectedSections: false };
  const byId = new Map<string, ImportItem>();

  let section: ImportSection = forceSection ?? 'have';
  let inRepetidasSection = forceSection === 'have';
  // Contexto de pais derivado de lineas previas (flag o nombre solo). Se aplica
  // a lineas que solo contienen numeros.
  let countryContext: string | null = null;

  const recordSticker = (
    prefix: string,
    slot: string,
    copiesValue: number,
    isExplicit: boolean,
    rawLabel?: string,
  ): void => {
    const stickerId = buildStickerId(prefix, slot);
    if (!stickerId || !VALID_STICKER_IDS.has(stickerId)) {
      result.unknown.push(rawLabel ?? `${prefix}${slot}`);
      return;
    }
    let copies = copiesValue;
    if (section === 'have' && inRepetidasSection && !isExplicit) {
      copies = assumeDouble ? 2 : 1;
    }
    if (section === 'want') {
      copies = 0;
    }
    byId.set(stickerId, { stickerId, copies, section });
  };

  const processSlotString = (prefix: string, slotsRaw: string): void => {
    const slotTokens = splitSlotString(slotsRaw);
    for (const slotRaw of slotTokens) {
      const token = parseSlotToken(slotRaw);
      if (token.kind === 'error') {
        result.errors.push(`${prefix}: ${slotRaw}`);
        continue;
      }
      const slots: { slot: string; copies: number }[] = [];
      if (token.kind === 'single') {
        slots.push({ slot: token.slot, copies: token.copies });
      } else {
        for (let n = token.from; n <= token.to; n++) {
          slots.push({ slot: String(n), copies: token.copies });
        }
      }
      for (const { slot, copies } of slots) {
        recordSticker(prefix, slot, copies, token.explicit);
      }
    }
  };

  const rawLines = text.split(/\r?\n/);

  for (const rawLine of rawLines) {
    // Detectar flag ANTES de strip para no perderlo.
    const flagCode = detectFlagInLine(rawLine);

    const stripped = stripDecorations(stripEmojis(rawLine));
    if (!stripped) {
      if (flagCode) countryContext = flagCode;
      continue;
    }

    const hasUrl = /\bhttps?:\/\//i.test(stripped);

    if (!forceSection) {
      if (isWantSectionHeader(stripped)) {
        section = 'want';
        inRepetidasSection = false;
        countryContext = null;
        result.detectedSections = true;
      } else if (isHaveSectionHeader(stripped)) {
        section = 'have';
        inRepetidasSection =
          /\brepetid[ao]s?\b/i.test(stripped) ||
          /\bsobran/i.test(stripped) ||
          /\bswaps?\b/i.test(stripped) ||
          /\btrades?\b/i.test(stripped) ||
          /\bintercambios?\b/i.test(stripped) ||
          /\bduplicates?\b/i.test(stripped) ||
          /\bspares?\b/i.test(stripped) ||
          /\bextras?\b/i.test(stripped);
        countryContext = null;
        result.detectedSections = true;
      }
    }

    if (hasUrl) continue;
    if (isIgnorableLine(stripped)) continue;

    const line = normalizeParenQuantity(stripped);

    // 1) Header pattern (codigo corto o nombre largo).
    const headerMatch = matchCountryHeader(line);
    if (headerMatch && headerMatch.rest.trim()) {
      processSlotString(headerMatch.prefix, headerMatch.rest);
      countryContext = headerMatch.prefix;
      continue;
    }
    if (headerMatch && !headerMatch.rest.trim()) {
      // Solo nombre/codigo sin slots → setea contexto.
      countryContext = headerMatch.prefix;
      continue;
    }

    // 2) Inline tokens: "MEX2 x3", "FWC9", "00 x1", "MEX1-5" mezclados.
    const inlineTokens = tokenizeInlineCodes(line);
    if (inlineTokens.length > 0) {
      for (const tok of inlineTokens) {
        recordSticker(tok.prefix, tok.slot, tok.copies, tok.explicit, tok.raw);
      }
      continue;
    }

    // 3) Linea solo numeros bajo contexto activo (flag o pais previo).
    const activeContext = flagCode ?? countryContext;
    if (activeContext && isSlotOnlyLine(line)) {
      processSlotString(activeContext, line);
      continue;
    }

    // 4) Si tenia flag pero no es slot-only, igual setea contexto.
    if (flagCode) {
      countryContext = flagCode;
    }
  }

  result.ok = Array.from(byId.values());
  return result;
}
